import { extractErrorMessage } from "../utils/acceptSuggestions";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import AIPowerSearch from "../components/AIPowerSearch";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { 
  Database, 
  Search, 
  Filter, 
  Download, 
  Upload, 
  RefreshCw, 
  ChevronRight,
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileSpreadsheet,
  Server,
  X
} from "lucide-react";
import { toast } from "sonner";

export default function ApplicationProfilesListPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [aiSearchResults, setAiSearchResults] = useState(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await api.get("/application-profile/tasks");
      if (response.data.success) {
        setTasks(response.data.data.tasks || []);
        setSummary(response.data.data.summary || {});
      }
    } catch (error) {
      toast.error("Failed to fetch application profiles");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get("/application-profile/import/template", {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "application_profile_import_template.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Template downloaded successfully");
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const handleExportInventory = async (format) => {
    try {
      toast.loading(`Exporting Infrastructure Inventory (${format.toUpperCase()})...`, { id: "inv-export" });
      const response = await api.get(`/application-profile/inventory/export/${format}`, {
        responseType: "blob"
      });
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const filename = `infrastructure_inventory_${ts}.${format}`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Infrastructure Inventory exported as ${format.toUpperCase()}`, { id: "inv-export" });
    } catch (error) {
      toast.error(`Failed to export inventory: ${error.response?.data?.detail || error.message}`, { id: "inv-export" });
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith(".csv") && !file.name.endsWith(".xlsx")) {
      toast.error("Please upload a CSV or Excel file");
      return;
    }

    setImportFile(file);

    // Read and preview the file
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await api.post("/application-profile/import/preview", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (response.data.success) {
        setImportPreview(response.data.data.preview || []);
        toast.success(`Found ${response.data.data.total_rows} records to import`);
      }
    } catch (error) {
      toast.error("Failed to parse file: " + (extractErrorMessage(error, "Unknown error")));
      setImportFile(null);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;

    setImporting(true);
    const formData = new FormData();
    formData.append("file", importFile);

    try {
      const response = await api.post("/application-profile/import", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (response.data.success) {
        toast.success(`Successfully imported ${response.data.data.imported_count} profiles`);
        if (response.data.data.errors?.length > 0) {
          toast.warning(`${response.data.data.errors.length} rows had errors`);
        }
        setShowImportDialog(false);
        setImportFile(null);
        setImportPreview([]);
        fetchTasks();
      }
    } catch (error) {
      toast.error("Import failed: " + (extractErrorMessage(error, "Unknown error")));
    } finally {
      setImporting(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: "secondary", icon: Clock, label: "Pending" },
      in_progress: { variant: "default", icon: RefreshCw, label: "In Progress" },
      completed: { variant: "default", icon: CheckCircle2, label: "Completed", className: "bg-emerald-500" },
      overdue: { variant: "destructive", icon: AlertTriangle, label: "Overdue" }
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className={config.className}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const filteredTasks = (aiSearchResults || tasks).filter(task => {
    const matchesSearch = !searchTerm || task.application_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         task.business_unit?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || task.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Application Profiles</h1>
          <p className="text-slate-600">Manage application profiles for IT-DRP planning</p>
        </div>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="export-infra-inventory-btn">
                <Server className="w-4 h-4 mr-2" />
                Export Infrastructure Inventory
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>Download Servers / VMs (Primary &amp; DR)</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => handleExportInventory("xlsx")}
                data-testid="export-infra-xlsx-btn"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" />
                Export as Excel (.xlsx)
                <span className="ml-auto text-xs text-slate-500">Styled + Summary</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleExportInventory("csv")}
                data-testid="export-infra-csv-btn"
              >
                <Download className="w-4 h-4 mr-2 text-sky-600" />
                Export as CSV (.csv)
                <span className="ml-auto text-xs text-slate-500">Flat list</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" onClick={handleDownloadTemplate}>
            <Download className="w-4 h-4 mr-2" />
            Download Template
          </Button>
          <Button onClick={() => setShowImportDialog(true)}>
            <Upload className="w-4 h-4 mr-2" />
            Bulk Import
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Profiles</p>
                <p className="text-2xl font-bold">{summary.total || 0}</p>
              </div>
              <Database className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending</p>
                <p className="text-2xl font-bold text-amber-600">{summary.pending || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">In Progress</p>
                <p className="text-2xl font-bold text-blue-600">{summary.in_progress || 0}</p>
              </div>
              <RefreshCw className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Completed</p>
                <p className="text-2xl font-bold text-emerald-600">{summary.completed || 0}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6 space-y-3">
          <AIPowerSearch
            module="application_profiles"
            onResults={(results) => setAiSearchResults(results)}
            onClear={() => setAiSearchResults(null)}
            placeholder='Try: "find all pending profiles" or "SAP applications"...'
          />
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by application name or business unit..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-profiles"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchTasks}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Profiles Table */}
      <Card>
        <CardHeader>
          <CardTitle>Application Profile Tasks</CardTitle>
          <CardDescription>Click on a row to open the application profile wizard</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : filteredTasks.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Database className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No application profiles found</p>
              <p className="text-sm">When BIAs are approved, tasks will appear here</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Application Name</TableHead>
                  <TableHead>Business Unit</TableHead>
                  <TableHead>RTO / RPO</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTasks.map((task) => (
                  <TableRow 
                    key={task.id} 
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => task.status !== 'completed' && navigate(`/application-profile/${task.id}`)}
                    data-testid={`profile-row-${task.id}`}
                  >
                    <TableCell className="font-medium">{task.application_name}</TableCell>
                    <TableCell>{task.business_unit}</TableCell>
                    <TableCell>
                      <span className="text-sm">
                        {task.required_rto}h / {task.required_rpo}h
                      </span>
                    </TableCell>
                    <TableCell>{getStatusBadge(task.status)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress value={task.completion_percentage || 0} className="w-20 h-2" />
                        <span className="text-sm text-slate-500">{task.completion_percentage || 0}%</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {task.due_date ? new Date(task.due_date).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>
                      {task.status !== 'completed' && (
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Bulk Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk Import Application Profiles</DialogTitle>
            <DialogDescription>
              Upload a CSV or Excel file to import multiple application profiles at once.
              Download the template first to ensure correct formatting.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* File Upload */}
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
              {importFile ? (
                <div className="flex items-center justify-center gap-4">
                  <FileSpreadsheet className="w-8 h-8 text-emerald-500" />
                  <div className="text-left">
                    <p className="font-medium">{importFile.name}</p>
                    <p className="text-sm text-slate-500">
                      {importPreview.length} records found
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => {
                    setImportFile(null);
                    setImportPreview([]);
                  }}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <Upload className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                  <p className="text-slate-600 mb-2">Drag and drop your file here, or</p>
                  <label>
                    <input
                      type="file"
                      accept=".csv,.xlsx"
                      onChange={handleFileSelect}
                      className="hidden"
                      data-testid="import-file-input"
                    />
                    <Button variant="outline" asChild>
                      <span className="cursor-pointer">Browse Files</span>
                    </Button>
                  </label>
                  <p className="text-sm text-slate-400 mt-2">Supports CSV and Excel files</p>
                </>
              )}
            </div>

            {/* Preview Table */}
            {importPreview.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-50 px-4 py-2 border-b">
                  <p className="font-medium text-sm">Preview (first 5 rows)</p>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Application Name</TableHead>
                        <TableHead>Business Unit</TableHead>
                        <TableHead>RTO</TableHead>
                        <TableHead>RPO</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {importPreview.slice(0, 5).map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{row.application_name}</TableCell>
                          <TableCell>{row.business_unit}</TableCell>
                          <TableCell>{row.required_rto}h</TableCell>
                          <TableCell>{row.required_rpo}h</TableCell>
                          <TableCell>
                            <Badge variant={row.valid ? "default" : "destructive"}>
                              {row.valid ? "Valid" : row.error}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleImport} 
              disabled={!importFile || importing}
              data-testid="confirm-import-btn"
            >
              {importing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Import {importPreview.length} Profiles
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
